import { Component, type ComponentType, type ReactNode } from 'react';
import { widgetVisible, WIDGETS, type WidgetInstance, type WidgetType } from '@ce/core';
import { Banner, CtaButton, MerchStrip, RichText, Spacer, TicketCta } from './content';
import { LiveFeedback, Poll, QuizCard, SessionList, SmartSuggestions } from './engage';
import {
  CallSheetWidget, Countdown, DayPicker, FeaturedTalent, JourneyCta, PromoRotatorWidget,
  QuickLinks, SpecialEvents, UpNext,
} from './today';
import type { WidgetCtx, WidgetProps } from './context';

/**
 * Type to component. Adding a widget is an entry in `@ce/core`'s catalogue plus
 * one line here; a type with no entry is skipped, never thrown on, so an older
 * app meeting a newer config simply leaves the unfamiliar widget out.
 */
const COMPONENTS: Record<WidgetType, ComponentType<WidgetProps>> = {
  dayPicker: DayPicker,
  upNext: UpNext,
  promoRotator: PromoRotatorWidget,
  quickLinks: QuickLinks,
  journeyCta: JourneyCta,
  callSheet: CallSheetWidget,
  featuredTalent: FeaturedTalent,
  specialEvents: SpecialEvents,
  countdown: Countdown,
  sessionList: SessionList,
  smartSuggestions: SmartSuggestions,
  poll: Poll,
  liveFeedback: LiveFeedback,
  quizCard: QuizCard,
  ticketCta: TicketCta,
  merchStrip: MerchStrip,
  banner: Banner,
  richText: RichText,
  ctaButton: CtaButton,
  spacer: Spacer,
};

/** One widget failing must not blank the page. */
class Boundary extends Component<{ id: string; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error: unknown) {
    console.warn(`[widgets] "${this.props.id}" failed to render and was skipped`, error);
  }
  render() { return this.state.failed ? null : this.props.children; }
}

function Host({ inst, ctx }: { inst: WidgetInstance; ctx: WidgetCtx }) {
  const Comp = COMPONENTS[inst.type];
  if (!Comp || !WIDGETS[inst.type]) return null;
  const highlighted = ctx.highlightId === inst.id;
  return (
    <div
      data-wid={inst.id}
      style={
        highlighted
          ? { outline: '2px solid var(--color-accent)', outlineOffset: 4, borderRadius: 12, transition: 'outline-color .2s ease' }
          : undefined
      }
    >
      <Boundary id={inst.id}><Comp ctx={ctx} inst={inst} /></Boundary>
    </div>
  );
}

/**
 * Renders a page from config.
 *
 * Each widget sits in its own wrapper so the screen-entrance stagger can address
 * direct children, and so the admin's preview has a stable `data-wid` to click.
 */
export function PageView({ pageId, ctx }: { pageId: string; ctx: WidgetCtx }) {
  const page = ctx.config.pages[pageId] ?? ctx.config.pages.home;
  const visible = page.widgets.filter((w) =>
    widgetVisible(w, ctx.config, { hasBadge: !!ctx.state.credential, day: ctx.state.day }),
  );
  return (
    <div className="stagger">
      {visible.map((inst) => <Host key={inst.id} inst={inst} ctx={ctx} />)}
      <div style={{ height: 26 }} />
    </div>
  );
}

export { COMPONENTS as WIDGET_COMPONENTS };
